/**
 * What a page shows while its code loads: the outline of a page, not a grey
 * block, and nothing at all for the first moment, so a page that arrives
 * quickly never flashes one.
 *
 * `fill` is for the full-height screens (a chat, a job, a deck), which have no
 * page padding of their own.
 */
import { Suspense, type ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useErrorCopy } from "./copy";

export function PageSkeleton({ fill = false, className }: { fill?: boolean; className?: string }) {
  const copy = useErrorCopy();
  return (
    <div
      role="status"
      aria-label={copy.loading}
      className={cn(
        "animate-in fade-in-0 fill-mode-both flex w-full flex-col gap-6 delay-150 duration-500",
        fill && "min-h-0 flex-1 p-4 sm:p-6",
        className,
      )}
    >
      <div className="flex flex-col gap-2.5">
        <Skeleton className="h-7 w-56 max-w-[70%]" />
        <Skeleton className="h-4 w-96 max-w-[90%]" />
      </div>
      {fill ? (
        <Skeleton className="min-h-64 w-full flex-1 rounded-xl" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="hidden h-24 rounded-xl sm:block" />
            <Skeleton className="hidden h-24 rounded-xl sm:block" />
          </div>
          <Skeleton className="h-72 w-full rounded-xl" />
        </>
      )}
    </div>
  );
}

/** A lazily loaded page, with the page outline while it loads. */
export function PageSuspense({ children, fill = false }: { children: ReactNode; fill?: boolean }) {
  return <Suspense fallback={<PageSkeleton fill={fill} />}>{children}</Suspense>;
}
